import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { ImageTransform } from "@/lib/imageUrl";

export function useSignedStorageUrl(
  bucket: string,
  path: string | null,
  expiresInSeconds = 3600,
  transform?: ImageTransform,
) {
  const query = useQuery({
    queryKey: ["signed-url", bucket, path, transform],
    enabled: !!path,
    // 4xx = file doesn't exist or no permission — retrying won't help
    retry: (failureCount, error: unknown) => {
      const status = (error as { status?: number })?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 3;
    },
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path!, expiresInSeconds, transform ? { transform } : undefined);
      if (error || !data?.signedUrl) {
        throw error ?? new Error("No signed URL returned");
      }
      return data.signedUrl;
    },
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
